import './assets/css/reset.css'
import './assets/css/main.css'
import './assets/css/typography.css'
import './assets/css/variables.css'

// App
import { supabase } from '@/supabase'
import messages from '@intlify/unplugin-vue-i18n/messages'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import InstantSearch from 'vue-instantsearch/vue3/es'
import App from './App.vue'
import router from './router'
import getCommerceLayer from '@/commercelayer'
import { roleBasedAccess } from '@/plugins/permissions'
import * as Sentry from '@sentry/vue'

// Shoelace
import '@shoelace-style/shoelace/dist/themes/light.css'
import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path'
import './assets/css/shoelace.css'

setBasePath('https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.20.1/dist/')

// Table Styles
import '@/assets/css/table.css'
import '@/assets/css/vue3-easy-data-table.css'

// Setup i18n
const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: 'de',
  fallbackLocale: 'de',
  availableLocales: ['de', 'ch_de', 'en', 'fr'],
  langDir: 'locales/',
  messages: messages
})

// Create app
const app = createApp(App)

// Sentry
Sentry.init({
  app,
  attachStacktrace: true,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // eslint-disable-next-line no-undef
  release: __APP_VERSION__,
  environment: import.meta.env.VITE_WEB_ENV,
  // Transient, non-actionable errors from flaky POS-device connectivity. The app already
  // handles these gracefully (401 retry, token refresh, sign-out, user alert) — they add
  // no signal and drown out real issues. Suppressing them here changes reporting only, not
  // app behavior. Covers the recurring Commerce Layer network/token noise:
  //   WINGS-5QQ / WINGS-51G — generic network transport failures (device offline). The dominant
  //     source (CustomerAppointmentCard) is now handled at the call site; these stay as a net
  //     for the long tail of other bare cl.* calls that can reject when the connection drops.
  //   WINGS-5DH / WINGS-5DC — edge function could not return a token
  //   WINGS-5DD / WINGS-5EH / WINGS-5DJ — edge function returned no readable token
  // NOTE: 'INVALID_TOKEN' (WINGS-5QM / WINGS-4MN) is intentionally NOT suppressed — its root
  // cause (the concurrent token-refresh race in commercelayer.js) is fixed, so we keep it
  // visible to confirm the fix holds and to surface any genuinely invalid/revoked tokens.
  ignoreErrors: [
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource',
    'The network connection was lost',
    'Failed to get commerce token',
    'Failed to fetch CommerceLayer token'
  ],
  // integrations: [
  //   Sentry.browserProfilingIntegration(),
  //   Sentry.browserTracingIntegration(),
  //   Sentry.vueIntegration({
  //     tracingOptions: {
  //       trackComponents: true,
  //       hooks: ['activate', 'create', 'unmount', 'mount', 'update'] // Track specific hooks
  //     }
  //   })
  // ],
  tracePropagationTargets: ['https://wings.shopviu.com', 'https://wings-test.shopviu.com'],
  // Performance Monitoring
  tracesSampleRate: 0.5, //  Capture 50% of the transactions
  // Profiling
  profilesSampleRate: 0.5, // Capture 50% of the transactions for profiling
  // Session Replay
  replaysSessionSampleRate: 0, // Disable Session Replay
  replaysOnErrorSampleRate: 0, // Disable Session Replay,
  normalizeDepth: 5
})

// eslint-disable-next-line no-undef
Sentry.setTag('app_version', __APP_VERSION__)

// Browser support check — the build targets Chrome 107 / Edge 107 / Firefox 104 /
// Safari 16 (iOS 16) and up (see vite.config.js build.target + browserslist).
const browserSupported =
  typeof window.structuredClone === 'function' &&
  typeof Object.hasOwn === 'function' &&
  typeof Promise.any === 'function' &&
  typeof Array.prototype.findLast === 'function'

Sentry.setTag('browser_supported', browserSupported)

if (!browserSupported) {
  Sentry.captureMessage('Unsupported browser detected', {
    level: 'info',
    tags: { browser_supported: false }
  })
}

let marketSettings = localStorage.getItem('marketSettings')

try {
  if (marketSettings) {
    marketSettings = JSON.parse(marketSettings)

    if (marketSettings && marketSettings.warehouse) {
      Sentry.setTag('marketSettings', marketSettings.warehouse)
    } else {
      // delete localstorage entry where key is marketSettings.
      localStorage.removeItem('marketSettings')
      // Handle case where warehouse is missing
      alert(
        'Error: Irgendwas ist schiefgelaufen. Bitte öffne das Hauptmenü und selektieren den Store neu.'
      )
    }
  }
} catch (error) {
  console.error('Error parsing marketSettings JSON:', error)
}
async function setup() {
  const initErrors = []
  try {
    const { cl, clUtils } = await getCommerceLayer()
    app.provide('cl', cl)
    app.provide('clUtils', clUtils)
  } catch (e) {
    await supabase.auth.signOut()
  }

  try {
    app.provide('supabase', supabase)
    app.provide('Sentry', Sentry)

    app.use(createPinia())
    app.use(router)
    app.use(i18n)
    app.use(InstantSearch)
    app.use(roleBasedAccess({ router }))
  } catch (e) {
    initErrors.push({
      message: 'error.init.general'
    })
    console.log(e)
  }

  app.provide('initErrors', initErrors)
  app.mount('#app')
}

setup()
